// src/main/content/contests/ContestMomentsAndMovers.js
//
// Two small narrative cards rendered side-by-side, both derived from the
// same timeline data the cumulative chart and race replay consume:
//
//   - Key Moments: a chronological list of lead changes ("X takes the early
//     lead", "Y overtakes X to take 1st"). We only count a lead change once
//     the leader has actually scored — until then everyone's tied at 0 and
//     "leader" would be noisy.
//
//   - Biggest Movers: employees with the largest rank change from the start
//     of the contest to the latest snapshot. Climbers (positive change) and
//     fallers (negative change) both appear; we sort by absolute movement
//     so the most dramatic stories surface.
//
// Both are pure functions of the timeline so the derivation lives here in
// the frontend — no need for a separate backend trip.

import React, { useMemo } from "react";
import dayjs from "dayjs";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import HistoryIcon from "@mui/icons-material/History";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { TIMELINE_COLORS } from "./ContestCumulativeChart";

// ---------- derivation ----------

// Sort employees by cumulative value at a given date index in the metric's
// "better" direction. Higher-is-better → descending; lower-is-better →
// ascending. Ties are broken deterministically by employee id so subsequent
// calls produce stable rankings (important for detecting lead "changes" —
// without a stable order we'd see false-positives between equal scores).
function rankAtIndex(employees, idx, lowerIsBetter) {
  const sorted = [...employees].sort((a, b) => {
    const av = a.cumulative?.[idx] ?? 0;
    const bv = b.cumulative?.[idx] ?? 0;
    if (av !== bv) return lowerIsBetter ? av - bv : bv - av;
    return a.id - b.id;
  });
  const rankById = {};
  sorted.forEach((e, i) => {
    rankById[e.id] = i + 1;
  });
  return { sorted, rankById };
}

// "Has anything happened yet?" — true when any employee has a non-zero
// cumulative at this index. Used to gate the "first leader" moment so we
// don't crown someone arbitrarily on day 0 when everyone is tied at 0.
// Direction-agnostic: for both higher-is-better (max > 0 → someone scored)
// and lower-is-better (max > 0 → someone has been late / missed shifts /
// etc., which means the still-zero employees are meaningfully leading).
function anyActivityAt(employees, idx) {
  for (const e of employees) {
    if ((e.cumulative?.[idx] ?? 0) > 0) return true;
  }
  return false;
}

function deriveKeyMoments(timeline, lowerIsBetter) {
  if (!timeline?.dates?.length || !timeline?.employees?.length) return [];
  const moments = [];
  let prevLeaderId = null;
  for (let i = 0; i < timeline.dates.length; i++) {
    // Don't emit leadership moments until at least one employee has any
    // accumulated activity — before that everyone's tied at 0 and the
    // "leader" is whichever id sorts first, which is arbitrary.
    if (!anyActivityAt(timeline.employees, i)) continue;
    const { sorted } = rankAtIndex(timeline.employees, i, lowerIsBetter);
    const leader = sorted[0];
    if (!leader) continue;
    const leaderValue = leader.cumulative?.[i] ?? 0;
    if (leader.id !== prevLeaderId) {
      moments.push({
        date: timeline.dates[i],
        leader,
        value: leaderValue,
        isFirstLead: prevLeaderId === null,
      });
      prevLeaderId = leader.id;
    }
  }
  return moments;
}

function deriveBiggestMovers(timeline, lowerIsBetter) {
  if (!timeline?.dates?.length || !timeline?.employees?.length) return [];
  const lastIdx = timeline.dates.length - 1;
  if (lastIdx < 1) return [];
  const startRanks = rankAtIndex(timeline.employees, 0, lowerIsBetter).rankById;
  const endRanks = rankAtIndex(timeline.employees, lastIdx, lowerIsBetter).rankById;

  // For higher-is-better contests, the cumulative>0 filter ensures we only
  // surface employees who actually participated. For lower-is-better, the
  // most-interesting movers are the ones who CHANGED RANK regardless of
  // their cumulative — including someone who started at rank 1 (tied at 0)
  // and fell to rank 10 after racking up events. So we drop the activity
  // filter for lower-is-better and rely on the rank-change filter (the
  // ``change !== 0`` step) to keep the list meaningful.
  const baseList = lowerIsBetter
    ? timeline.employees
    : timeline.employees.filter((e) => (e.cumulative?.[lastIdx] ?? 0) > 0);

  return baseList
    .map((e, originalIndex) => ({
      employee: e,
      startRank: startRanks[e.id],
      endRank: endRanks[e.id],
      // Positive change = climbed (started at a worse rank, now better).
      change: startRanks[e.id] - endRanks[e.id],
      colorIndex: originalIndex,
    }))
    .filter((m) => m.change !== 0)
    .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
    .slice(0, 5);
}

// ---------- small UI ----------

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0][0] || "?").toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function Avatar({ name, colorIndex }) {
  const color = TIMELINE_COLORS[colorIndex % TIMELINE_COLORS.length];
  return (
    <div
      className="w-8 h-8 rounded-full text-white text-xs font-semibold flex items-center justify-center shrink-0"
      style={{ backgroundColor: color }}
    >
      {initials(name)}
    </div>
  );
}

function MomentRow({ moment, showStoreTag }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
        <EmojiEventsIcon className="text-amber-500" style={{ fontSize: 16 }} />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
          {dayjs(moment.date).format("MMM D")}
        </div>
        <div className="text-sm text-slate-900 mt-0.5">
          <span className="font-semibold">
            {moment.leader.full_name}
          </span>{" "}
          <span className="text-slate-600">
            {moment.isFirstLead ? "takes the early lead" : "takes the lead"}
          </span>
        </div>
        {showStoreTag && moment.leader.store_name && (
          <div className="text-[11px] text-slate-400 mt-0.5">
            {moment.leader.store_name}
          </div>
        )}
      </div>
    </div>
  );
}

function MoverRow({ mover, showStoreTag }) {
  const climbed = mover.change > 0;
  const Arrow = climbed ? TrendingUpIcon : TrendingDownIcon;
  const arrowCls = climbed ? "text-emerald-600" : "text-rose-500";
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <Avatar name={mover.employee.full_name} colorIndex={mover.colorIndex} />
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-900 truncate">
            {mover.employee.full_name}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 truncate">
            {showStoreTag && mover.employee.store_name
              ? `${mover.employee.store_name} · #${mover.startRank} → #${mover.endRank}`
              : `#${mover.startRank} → #${mover.endRank}`}
          </div>
        </div>
      </div>
      <div className={`flex items-center gap-1 text-sm font-semibold ${arrowCls}`}>
        <Arrow style={{ fontSize: 16 }} />
        {Math.abs(mover.change)}
      </div>
    </div>
  );
}

// ---------- standalone cards ----------
//
// Each card is exported individually so the parent can place them in
// whatever grid it wants (we currently render them alongside the cumulative
// chart as three equal columns). Each one self-hides if its derivation
// produces nothing, so the grid cell falls back to empty rather than an
// awkward "nothing happened yet" card.

export function ContestKeyMoments({
  timeline,
  showStoreTag = false,
  lowerIsBetter = false,
}) {
  const moments = useMemo(
    () => deriveKeyMoments(timeline, lowerIsBetter),
    [timeline, lowerIsBetter]
  );
  if (moments.length === 0) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden h-full">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        <HistoryIcon className="text-violet-600" style={{ fontSize: 20 }} />
        <h3 className="text-base font-semibold text-slate-900">Key Moments</h3>
      </div>
      <div className="px-5 py-4">
        <div className="space-y-4">
          {moments.map((m, idx) => (
            <MomentRow
              key={`${m.date}-${m.leader.id}-${idx}`}
              moment={m}
              showStoreTag={showStoreTag}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function ContestBiggestMovers({
  timeline,
  showStoreTag = false,
  lowerIsBetter = false,
}) {
  const movers = useMemo(
    () => deriveBiggestMovers(timeline, lowerIsBetter),
    [timeline, lowerIsBetter]
  );
  if (movers.length === 0) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden h-full">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        <SwapVertIcon className="text-violet-600" style={{ fontSize: 20 }} />
        <h3 className="text-base font-semibold text-slate-900">Biggest Movers</h3>
      </div>
      <div className="px-5 py-4">
        <div className="space-y-4">
          {movers.map((m) => (
            <MoverRow key={m.employee.id} mover={m} showStoreTag={showStoreTag} />
          ))}
        </div>
      </div>
    </div>
  );
}
